import apiClient from './apiClient';
import {
  PayrollConnection,
  PayrollConnectionCreated,
  PayrollConnectionInput,
  PayrollConnectionUpdate,
  PayrollSyncRun,
  PayrollSyncRunPage,
} from '../types/payrollIntegration';

/**
 * A conexão de folha de uma empresa.
 *
 * `create` é o único ponto em que a chave em claro existe: a resposta a traz uma vez, e nenhuma
 * outra rota a devolve. Quem consome precisa mostrá-la na hora — guardá-la em estado que
 * sobreviva ao diálogo seria guardar segredo em memória de tela sem motivo.
 */
class PayrollIntegrationService {
  private baseUrl(companyId: string): string {
    return `/companies/${companyId}/payroll-connections`;
  }

  async list(companyId: string): Promise<PayrollConnection[]> {
    const response = await apiClient.get<PayrollConnection[]>(this.baseUrl(companyId));
    return response.data ?? [];
  }

  async create(
    companyId: string,
    input: PayrollConnectionInput
  ): Promise<PayrollConnectionCreated> {
    const response = await apiClient.post<PayrollConnection>(this.baseUrl(companyId), input);
    const corpo = response as unknown as { data: PayrollConnection; chave: string };

    return { connection: corpo.data, chave: corpo.chave };
  }

  async update(
    companyId: string,
    connectionId: string,
    input: PayrollConnectionUpdate
  ): Promise<PayrollConnection> {
    const response = await apiClient.patch<PayrollConnection>(
      `${this.baseUrl(companyId)}/${connectionId}`,
      input
    );

    // A rota devolve a conexão atualizada; um corpo sem `data` é resposta fora do contrato, e
    // seguir com `undefined` faria a lista da tela perder a linha sem dizer por quê.
    if (!response.data) throw new Error('Resposta sem a conexão atualizada');
    return response.data;
  }

  async remove(companyId: string, connectionId: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl(companyId)}/${connectionId}`);
  }

  /** Roda uma carga sintética por dentro do backend — sem terminal, sem chave em claro. */
  async test(companyId: string, connectionId: string): Promise<{ ok: boolean; message: string }> {
    const response = await apiClient.post<{ ok: boolean; message: string }>(
      `${this.baseUrl(companyId)}/${connectionId}/test`
    );
    return (response as unknown as { ok: boolean; message: string });
  }

  async syncRuns(
    companyId: string,
    connectionId: string,
    page = 1,
    limit = 20
  ): Promise<PayrollSyncRunPage> {
    const response = await apiClient.get<PayrollSyncRun[]>(
      `${this.baseUrl(companyId)}/${connectionId}/sync-runs?page=${page}&limit=${limit}`
    );
    const corpo = response as unknown as {
      data: PayrollSyncRun[];
      pagination?: { total: number };
    };

    return { runs: corpo.data ?? [], total: corpo.pagination?.total ?? 0 };
  }
}

export const payrollIntegrationService = new PayrollIntegrationService();
export default payrollIntegrationService;
